import {
  forceHomeSplitFromUrl,
  getHistoryNavigationState,
  isAvatarFullNavigationState,
  isAvatarHomeSplitContext,
  normalizeContentPath,
} from './avatarActions';

const GUARD_FLAG = '__avatarHomeNavGuardInstalled';

function hrefFromUrl(url: string | URL): string {
  return typeof url === 'string' ? url : url.toString();
}

function interceptHomeNavigation(href: string): boolean {
  if (!isAvatarHomeSplitContext()) return false;
  if (forceHomeSplitFromUrl(href)) return true;

  try {
    const parsed = new URL(href, window.location.origin);
    if (parsed.origin !== window.location.origin) return false;
    const path = normalizeContentPath(parsed.pathname);
    if (path === '/' || path === '') return false;
    return forceHomeSplitFromUrl(path) || forceHomeSplitFromUrl(parsed.href);
  } catch {
    return false;
  }
}

/**
 * When Milan (or host-tools) navigates on `/`, load the page in the right split panel
 * instead of leaving the avatar view.
 */
export function installAvatarHomeNavigationGuard(): () => void {
  if ((window as unknown as Record<string, boolean>)[GUARD_FLAG]) {
    return () => undefined;
  }
  (window as unknown as Record<string, boolean>)[GUARD_FLAG] = true;

  const originalAssign = Location.prototype.assign;
  const originalReplace = Location.prototype.replace;
  const originalPushState = history.pushState.bind(history);
  const originalReplaceState = history.replaceState.bind(history);

  Location.prototype.assign = function assign(url: string | URL) {
    const href = hrefFromUrl(url);
    if (interceptHomeNavigation(href)) return;
    return originalAssign.call(this, url);
  };

  Location.prototype.replace = function replace(url: string | URL) {
    const href = hrefFromUrl(url);
    if (interceptHomeNavigation(href)) return;
    return originalReplace.call(this, url);
  };

  history.pushState = function pushState(
    data: unknown,
    unused: string,
    url?: string | URL | null
  ) {
    if (isAvatarFullNavigationState(getHistoryNavigationState(data))) {
      return originalPushState(data, unused, url);
    }
    if (url != null) {
      const href = hrefFromUrl(url);
      if (interceptHomeNavigation(href)) return;
    }
    return originalPushState(data, unused, url);
  };

  history.replaceState = function replaceState(
    data: unknown,
    unused: string,
    url?: string | URL | null
  ) {
    if (isAvatarFullNavigationState(getHistoryNavigationState(data))) {
      return originalReplaceState(data, unused, url);
    }
    if (url != null) {
      const href = hrefFromUrl(url);
      if (interceptHomeNavigation(href)) return;
    }
    return originalReplaceState(data, unused, url);
  };

  return () => {
    Location.prototype.assign = originalAssign;
    Location.prototype.replace = originalReplace;
    history.pushState = originalPushState;
    history.replaceState = originalReplaceState;
    delete (window as unknown as Record<string, boolean>)[GUARD_FLAG];
  };
}
